import { useEffect, useState } from "react";
import type { StudioAssignableUserV1, StudioWorkItemV1 } from "@enrein/shared-contracts";
import type { StudioApiClient } from "../api.js";

export type Block1WorkAssignmentProps = {
  readonly api: StudioApiClient;
  readonly projectId: string;
  readonly canMutate: boolean;
};

export function Block1WorkAssignment(props: Block1WorkAssignmentProps) {
  const { api, projectId, canMutate } = props;
  const [items, setItems] = useState<StudioWorkItemV1[]>([]);
  const [users, setUsers] = useState<StudioAssignableUserV1[]>([]);
  const [title, setTitle] = useState("");
  const [assigneeUserId, setAssigneeUserId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [nextAction, setNextAction] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    setError(null);
    try {
      const list = await api.listStudioWorkItems(projectId);
      setItems(list.items);
      if (canMutate) {
        const assignable = await api.listAssignableAgencyUsers(projectId);
        setUsers(assignable.items);
        if (!assigneeUserId && assignable.items[0]) {
          setAssigneeUserId(assignable.items[0].userId);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "network_error");
      setNextAction("Retry loading work assignments. Check tenant/project access.");
    }
  }

  useEffect(() => {
    void reload();
    // reload closes over projectId/canMutate; intentional mount+dependency refresh only.
  }, [projectId, canMutate]);

  async function onCreate() {
    if (!canMutate) {
      setError("permission_denied");
      setNextAction("Your role cannot assign work. Ask an operator.");
      return;
    }
    if (!title.trim()) {
      setError("validation_error");
      setNextAction("Enter a work item title.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.createStudioWorkItem(projectId, {
        schemaId: "studio.workItemCreateRequest.v1",
        title: title.trim(),
        assigneeUserId: assigneeUserId || undefined,
      });
      setTitle("");
      setNextAction(result.nextAction ?? "Work item saved.");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "network_error");
      setNextAction("Fix the assignment request and retry.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="v10-panel" aria-label="Team assignment">
      <h3>Team assignment</h3>
      <p className="v10-muted">Assign project work with least-privilege operator controls. Server authorization is authoritative.</p>
      {error ? <p role="alert">{error}</p> : null}
      {nextAction ? <p className="v10-muted">{nextAction}</p> : null}
      {canMutate ? (
        <div className="v10-form-grid">
          <div>
            <label htmlFor="ag006-title">Work item</label>
            <input id="ag006-title" value={title} disabled={busy} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label htmlFor="ag006-assignee">Assignee</label>
            <select
              id="ag006-assignee"
              value={assigneeUserId}
              disabled={busy || users.length === 0}
              onChange={(e) => setAssigneeUserId(e.target.value)}
            >
              {users.length === 0 ? <option value="">No assignable users</option> : null}
              {users.map((user) => (
                <option key={user.userId} value={user.userId}>
                  {user.name || user.email} ({user.role})
                </option>
              ))}
            </select>
          </div>
          <div>
            <button type="button" disabled={busy} onClick={() => void onCreate()}>
              Create & assign
            </button>
          </div>
        </div>
      ) : (
        <p className="v10-muted">View only — your role cannot mutate assignments.</p>
      )}
      <ul aria-label="Work items">
        {items.length === 0 ? <li className="v10-muted">No work items yet.</li> : null}
        {items.map((item) => (
          <li key={item.id}>
            <strong>{item.title}</strong> — {item.status}
            {item.assigneeUserId ? ` · assignee ${item.assigneeUserId}` : " · unassigned"}
          </li>
        ))}
      </ul>
    </section>
  );
}
